import { StyleSheet } from 'react-native';

// Color palette
export const colors = {
  primary: '#4a3975',
  primaryLight: '#667eea',
  primaryDark: '#2d1b3d',
  white: '#ffffff',
  background: 'rgba(255, 255, 255, 0.98)',
  cardBackground: 'rgba(255, 255, 255, 0.92)',
  glassMorphism: 'rgba(255, 255, 255, 0.15)',
  glassMorphismStrong: 'rgba(255, 255, 255, 0.25)',
  textPrimary: '#2c1810',
  textSecondary: '#666666',
  textLight: 'rgba(255, 255, 255, 0.9)',
  textVeryLight: 'rgba(255, 255, 255, 0.7)',
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#F44336',
  info: '#2196F3',
  purple: '#9C27B0',
  gradientStart: '#667eea',
  gradientEnd: '#764ba2',
};

// Spacing scale
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
  huge: 40,
};

// Border radius scale
export const borderRadius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  pill: 50,
};

// Shadow presets
export const shadows = {
  card: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 8,
  },
  button: {
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  input: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  result: {
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
  },
};

// Typography scale
export const typography = {
  heading1: {
    fontSize: 28,
    fontWeight: '800',
    lineHeight: 34,
  },
  heading2: {
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 30,
  },
  heading3: {
    fontSize: 20,
    fontWeight: '600',
    lineHeight: 26,
  },
  body: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
  },
  bodySmall: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
  caption: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
  },
  button: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
};

// Common component styles
export const commonStyles = StyleSheet.create({
  // Glass-morphism containers
  glassCard: {
    backgroundColor: colors.glassMorphism,
    borderRadius: borderRadius.xl,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(20px)',
    ...shadows.card,
  },
  
  solidCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: borderRadius.xl,
    borderWidth: 1,
    borderColor: 'rgba(116, 75, 162, 0.1)',
    ...shadows.card,
  },

  // Input field styles
  modernInput: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.md,
    ...shadows.input,
  },

  // Button styles
  primaryButton: {
    backgroundColor: colors.primary,
    borderRadius: borderRadius.xl,
    paddingHorizontal: spacing.xxl,
    paddingVertical: spacing.lg,
    ...shadows.button,
  },

  gradientButton: {
    borderRadius: borderRadius.xl,
    paddingHorizontal: spacing.xxl,
    paddingVertical: spacing.lg,
    ...shadows.button,
  },

  // Result styles
  resultCard: {
    backgroundColor: colors.background,
    borderRadius: borderRadius.xl,
    padding: spacing.xxl,
    marginBottom: spacing.xl,
    borderWidth: 1,
    borderColor: 'rgba(116, 75, 162, 0.15)',
    ...shadows.result,
  },

  // Score display styles
  scoreCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 6,
    marginBottom: spacing.xl,
  },

  // Chip styles
  modernChip: {
    backgroundColor: 'rgba(74, 57, 117, 0.1)',
    borderRadius: borderRadius.pill,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: 'rgba(74, 57, 117, 0.2)',
    ...shadows.input,
  },

  // Text styles
  headerTitle: {
    ...typography.heading1,
    color: colors.textPrimary,
    textAlign: 'center',
    marginBottom: spacing.md,
  },

  headerSubtitle: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xxl,
  },

  cardTitle: {
    ...typography.heading2,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },

  sectionTitle: {
    ...typography.heading3,
    color: colors.primary,
    marginBottom: spacing.lg,
  },

  bodyText: {
    ...typography.body,
    color: colors.textPrimary,
  },

  secondaryText: {
    ...typography.bodySmall,
    color: colors.textSecondary,
  },

  // Layout styles
  container: {
    flex: 1,
  },

  centeredContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.xl,
  },

  scrollContainer: {
    flex: 1,
    padding: spacing.lg,
  },

  formContainer: {
    width: '100%',
    maxWidth: 500,
    alignSelf: 'center',
    marginBottom: spacing.xxl,
  },

  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.lg,
    marginTop: spacing.xl,
  },

  resultsContainer: {
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
  },
});

// Responsive breakpoints
export const breakpoints = {
  mobile: 768,
  tablet: 1024,
};

// Animation presets
export const animations = {
  fast: 200,
  normal: 300,
  slow: 500,
  bounce: 'spring',
  ease: 'ease-in-out',
};

// Utility functions for responsive design
export const getResponsiveValue = (mobile, tablet, desktop, screenWidth) => {
  if (screenWidth >= breakpoints.tablet) return desktop || tablet || mobile;
  if (screenWidth >= breakpoints.mobile) return tablet || mobile;
  return mobile;
};

export const getCardMaxWidth = (screenWidth) => {
  return getResponsiveValue(350, 500, 600, screenWidth);
};

export const getPadding = (screenWidth) => {
  return getResponsiveValue(spacing.lg, spacing.xl, spacing.xxl, screenWidth);
};